import { reconnectWithNewAuth, getConfig, deriveUserIdentityFromIdToken, getActiveSessionManager, onSessionCleared } from '@bounded-sh/core';
import { getAuthProvider, login as authLogin, logout as authLogout, clearIncompatibleSession, getCurrentAuthMethod } from './auth';
import { init as configInit } from '@bounded-sh/core';
/**
 * Subscribed once per process, not per init(): re-registering on every init would
 * stack duplicate handlers on repeated initialization.
 */
let sessionClearedBridgeRegistered = false;
function registerSessionClearedBridge() {
    if (sessionClearedBridgeRegistered)
        return;
    sessionClearedBridgeRegistered = true;
    onSessionCleared(() => {
        // Core fires this only when storage was ACTUALLY cleared, never on a
        // transient refresh failure that preserved the session — so this cannot
        // sign anyone out over a network blip.
        setCurrentUser(null);
    });
}
/**
 * Read the active session's idToken synchronously from whichever store is live
 * for this runtime (WebSessionManager on web, ReactNativeSessionManager on RN —
 * selected by getActiveSessionManager, the same selector the core request path
 * uses). Returns null when there's no session — the caller leaves the user as-is.
 */
function getCurrentIdToken() {
    try {
        return getActiveSessionManager().getIdToken();
    }
    catch ( /* no session / store unavailable */_a) { /* no session / store unavailable */ }
    return null;
}
/**
 * Enrich a freshly-produced User with the universal identity fields
 * ({ id, email }) derived from the active session's idToken. ADDITIVE +
 * backwards-compatible: `address` is left exactly as the provider set it, and
 * id/email are only populated when not already present. For wallet logins
 * id === address; for email-only (Bounded) logins address may be null and id
 * is the account identity. Mirrors the realtime-worker auth.ts resolution so
 * the client `user` matches what the backend authenticates as (@user.id / .email).
 */
function withUserIdentity(user) {
    var _a, _b, _c, _d;
    if (!user)
        return user;
    const { id, email } = deriveUserIdentityFromIdToken(getCurrentIdToken());
    return Object.assign(Object.assign({}, user), { 
        // Prefer an id the provider already set; otherwise fall back to the
        // token-derived identity, then to the wallet address (wallet === id).
        id: (_c = (_b = (_a = user.id) !== null && _a !== void 0 ? _a : id) !== null && _b !== void 0 ? _b : user.address) !== null && _c !== void 0 ? _c : undefined, 
        // Email comes only from email-login tokens; keep any provider-set value.
        email: user.email !== undefined ? user.email : email, 
        // Guest/anonymous flag (Firebase `isAnonymous` parity): authoritative
        // source is the active auth method, so it's correct on fresh login AND
        // after a session restore (the stored method is rehydrated before the
        // user is set). A provider may also set it explicitly; honor that first.
        isAnonymous: (_d = user.isAnonymous) !== null && _d !== void 0 ? _d : (getCurrentAuthMethod() === 'guest') });
}
let authProviderInstance = null;
let currentUser = null;
let authStateListeners = [];
let authLoadingListeners = [];
let initCompleted = false;
let isAuthLoading = false;
let authReadyPromise = null;
// This file acts as a middleware for the global state of the SDK
// This mostly involves setting up the AuthProvider and managing the current user
async function initialize(newConfig) {
    // Mark auth as loading for the ENTIRE init/restore window so consumers render
    // a neutral/loading state instead of the signed-out UI while we rehydrate the
    // session — otherwise getCurrentUser() is null + loading is false during the
    // async restore, which paints "logged out" and then flips to "logged in" (an
    // auth flash). The React useAuth hook seeds loading=true itself, but the
    // imperative API (onAuthLoadingChanged/getAuthLoading) relies on this flag, so
    // setting it here makes "no flash" the SDK's default behavior for every consumer.
    setAuthLoading(true);
    try {
        // Bridge core's "the stored session is gone" signal into SDK auth state.
        //
        // Core cannot import this package, and a session can die deep inside a
        // session manager (dead refresh token, rejected refresh, app-id mismatch).
        // Before this, setCurrentUser(null) was only ever called by logout() and
        // init(), so storage could be wiped while `currentUser` stayed populated —
        // onAuthStateChanged never fired, and every consumer UI went on showing a
        // connected wallet for a session the server no longer knew about.
        registerSessionClearedBridge();
        // Get auth provider first
        authProviderInstance = await getAuthProvider(newConfig);
        // Initialize config with auth provider
        await configInit(Object.assign(Object.assign({}, newConfig), { authProvider: authProviderInstance }));
        // Now that core is initialized, update the auth method on the config
        const authMethod = getCurrentAuthMethod();
        if (authMethod) {
            const coreConfig = await getConfig();
            coreConfig.authMethod = authMethod;
        }
        // Wait for restoreSession to complete
        // The appId check is done in SessionManager.getSession which is called by restoreSession
        // If there's an appId mismatch, the session will be cleared and restoreSession will return null
        let user = await authProviderInstance.restoreSession();
        // If a session exists but was created before auth method tracking (legacy session),
        // clear it to avoid mismatches when the app switches auth methods (e.g. email → phantom)
        //
        // AWAITED: the clear is serialized through a cross-tab lock now, and an
        // unawaited call would return a Promise (always truthy) — nulling the user
        // unconditionally AND letting setCurrentUser run while the old credential
        // was still in storage.
        if (user && await clearIncompatibleSession()) {
            user = null;
        }
        setCurrentUser(user);
        // Mark init as completed
        initCompleted = true;
    }
    finally {
        // Resolve loading once the user is known (or restore failed) — listeners
        // registered mid-init still get the correct final state via the immediate
        // callback in onAuthLoadingChanged.
        setAuthLoading(false);
    }
}
export function init(newConfig) {
    let resolveReady;
    let rejectReady;
    const ready = new Promise((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
    });
    // Publish readiness before initialization starts. This makes it safe for an
    // auth-loading listener to call waitForAuthReady() synchronously when init()
    // announces that session restoration has begun.
    authReadyPromise = ready;
    void initialize(newConfig).then(resolveReady, rejectReady);
    return ready;
}
/**
 * Wait for the current init() call, including stored-session restoration.
 *
 * The promise rejects with the original initialization error. Once it resolves,
 * the returned user is the authoritative restored user, or null when signed out.
 */
export async function waitForAuthReady() {
    const ready = authReadyPromise;
    if (!ready) {
        throw new Error('SDK not initialized. Please call init() first.');
    }
    await ready;
    return currentUser;
}
export function onAuthStateChanged(callback) {
    authStateListeners.push(callback);
    if (initCompleted || hasRunFirstTime) {
        // Call the callback immediately with the current user.
        // We check hasRunFirstTime in addition to initCompleted to handle the race
        // where the Phantom auto-login effect calls setCurrentUser() before init()
        // finishes. Without this, listeners registered between auto-login's
        // setCurrentUser and init()'s initCompleted would never fire (init's
        // setCurrentUser is a no-op for same-address and initCompleted is still false).
        callback(currentUser);
    }
    return () => {
        authStateListeners = authStateListeners.filter((listener) => listener !== callback);
    };
}
export function onAuthLoadingChanged(callback) {
    authLoadingListeners.push(callback);
    // Call immediately with current loading state
    callback(isAuthLoading);
    return () => {
        authLoadingListeners = authLoadingListeners.filter((listener) => listener !== callback);
    };
}
export function setAuthLoading(loading) {
    if (isAuthLoading !== loading) {
        isAuthLoading = loading;
        authLoadingListeners.forEach((callback) => callback(loading));
    }
}
export function getAuthLoading() {
    return isAuthLoading;
}
export async function login(options) {
    if (!authProviderInstance) {
        throw new Error('SDK not initialized. Please call init() first.');
    }
    const loggedInUser = await authLogin(options);
    setCurrentUser(loggedInUser);
    return currentUser;
}
export async function logout(options) {
    if (!authProviderInstance) {
        throw new Error('SDK not initialized. Please call init() first.');
    }
    await authLogout(options);
    setCurrentUser(null);
}
var hasRunFirstTime = false;
function userPrincipalKey(user) {
    var _a, _b, _c;
    if (!user)
        return null;
    return (_c = (_b = (_a = user.id) !== null && _a !== void 0 ? _a : user.address) !== null && _b !== void 0 ? _b : user.email) !== null && _c !== void 0 ? _c : null;
}
// Update the currentUser and notify listeners
export function setCurrentUser(user) {
    // Enrich with the universal identity fields (id/email) derived from the
    // active session's idToken — central chokepoint so every provider's user
    // object gains { id, address, email } without each construction site
    // having to set them.
    user = withUserIdentity(user);
    const lastUser = currentUser;
    const wasInitializedBefore = hasRunFirstTime;
    const lastPrincipal = userPrincipalKey(currentUser);
    const nextPrincipal = userPrincipalKey(user);
    const shouldRunListeners = !hasRunFirstTime || lastPrincipal !== nextPrincipal;
    hasRunFirstTime = true;
    currentUser = user;
    if (shouldRunListeners) {
        // Isolate each listener: a consumer callback that throws (during logout in
        // particular) must not abort the notification loop OR skip the socket reopen
        // below. Before this, a single throwing listener propagated out of
        // setCurrentUser, so the realtime socket kept streaming as the signed-out
        // user and the other listeners were never told the principal changed.
        authStateListeners.forEach((callback) => {
            try {
                callback(user);
            }
            catch (error) {
                console.error('Error in onAuthStateChanged listener:', error);
            }
        });
        // Reconnect WebSocket connections with new auth when user changes.
        // Skip on the first setCurrentUser call during init() — there are no
        // existing connections to reconnect at that point.
        if (wasInitializedBefore && userPrincipalKey(lastUser) !== nextPrincipal) {
            reconnectWithNewAuth().catch((error) => {
                console.error('Error reconnecting WebSocket after auth change:', error);
            });
        }
    }
    // Return this commit's normalized snapshot. A listener can start another
    // login, so the mutable currentUser may already belong to a newer session.
    return user;
}
export function getCurrentUser() {
    return currentUser;
}
/** @internal Snapshot/restore surface for transactional auth-provider switches. */
export function getAuthProviderInstance() {
    return authProviderInstance;
}
export function setAuthProviderInstance(provider) {
    authProviderInstance = provider;
}
